import { useContext, useEffect, useState } from 'react';
import { Button } from 'components/ui/button';
import { AppContext } from 'contexts/AppContext';
import { useOverviewData } from 'hooks/useOverviewData';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { Activity, Wrench, TrendingUp, Info, Filter, AlertTriangle } from 'lucide-react';

/**
 * Overview — is it used, and for what?
 *
 * The mock's headline panel is an open → message → product → cart funnel. It is
 * NOT built here, and the card says why: opens ARE recorded, but they cannot be
 * joined to messages — the two sides used different session-id namespaces. See
 * the note on that card and overview_retrieval's docstring.
 */

const fmt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());
const secs = (n) => (n === null || n === undefined ? '—' : `${Number(n).toFixed(1)}s`);

/**
 * Headline figure with a period-over-period delta.
 *
 * `lowerIsBetter` matters: a 20% rise in p95 latency is bad news wearing the
 * same green arrow as a 20% rise in sessions. Latency inverts.
 */
function Stat({ label, value, deltaPct, lowerIsBetter = false, sub, muted = false, deltaUnit = '%', deltaVs = 'previous period' }) {
  const hasDelta = deltaPct !== null && deltaPct !== undefined;
  const improving = hasDelta && (lowerIsBetter ? deltaPct < 0 : deltaPct > 0);
  const worsening = hasDelta && (lowerIsBetter ? deltaPct > 0 : deltaPct < 0);
  // When the window is too short to trust, the arrow still points the right way
  // but loses its colour. A red 17% on two weeks of this traffic reads as a
  // finding; it is usually just which days fell inside the window.
  const colour = muted
    ? 'text-gray-400'
    : improving ? 'text-emerald-600' : worsening ? 'text-red-600' : 'text-gray-500';

  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 p-4">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className="text-2xl font-medium">{value}</div>
      {hasDelta ? (
        <div className={`text-xs mt-1 ${colour}`}>
          {deltaPct > 0 ? '↑' : deltaPct < 0 ? '↓' : ''} {Math.abs(deltaPct)}{deltaUnit} vs {deltaVs}
        </div>
      ) : (
        <div className="text-xs mt-1 text-gray-400">
          {sub || 'no previous period to compare'}
        </div>
      )}
      {/* The caption is not an either/or with the delta. It used to be -- so
          "N by voice" and "text turns only" could only ever render on a card
          with no previous period, which in practice meant never. */}
      {hasDelta && sub && (
        <div className="text-xs mt-0.5 text-gray-400">{sub}</div>
      )}
    </div>
  );
}

export default function OverviewTab() {
  const { selectedClient, dateRange, platformFilter, languageFilter } = useContext(AppContext);
  const {
    summary, summaryLoading, summaryError, fetchSummary,
    trend, trendLoading, fetchTrend,
    tools, toolsLoading, fetchTools,
  } = useOverviewData();

  // 'all' by default: the tab describes ALL traffic unless the reader chooses.
  // Three states -- the third shows ONLY the internal list's accounts, for
  // watching our own testing without customer noise.
  const [audience, setAudience] = useState('all');
  const excludeInternal = audience === 'customers';
  const internalOnly = audience === 'internal';

  useEffect(() => {
    fetchSummary({ excludeInternal, internalOnly });
    fetchTrend({ excludeInternal, internalOnly });
    fetchTools({ excludeInternal, internalOnly });
  }, [selectedClient, dateRange, platformFilter, languageFilter, audience,
      excludeInternal, internalOnly, fetchSummary, fetchTrend, fetchTools]);

  // "vs previous period" was the top question the cards generated. The
  // backend already names the exact baseline (compared_with, rendered under
  // the grid); this puts the ANSWER on the card itself: previous N days.
  const deltaVs = summary?.range_days
    ? `previous ${summary.range_days} days`
    : 'previous period';

  // Overview wants the coarse shape, not Quality's full per-tool table.
  const topTools = (tools || []).slice(0, 8);
  const totalCalls = (tools || []).reduce((a, t) => a + (t.calls || 0), 0);

  return (
    <div className="space-y-5">

      <div className="flex items-center gap-2">
        {[
          { v: 'all', l: 'All Traffic' },
          { v: 'customers', l: 'Customers Only' },
          { v: 'internal', l: 'Internal Only' },
        ].map((o) => (
          <Button key={o.v} size="sm"
                  variant={audience === o.v ? 'default' : 'outline'}
                  onClick={() => setAudience(o.v)}>
            {o.l}
          </Button>
        ))}
        <span className="text-xs text-gray-500">
          {audience === 'customers'
            ? 'Excluding internal accounts.'
            : audience === 'internal'
              ? 'Internal accounts only — staff and test traffic on the known list.'
              : 'Including internal accounts.'}
        </span>
      </div>

      {summaryError && <div className="text-sm text-red-600">{summaryError}</div>}
      {summaryLoading && <div className="text-sm text-gray-500 py-6">Loading…</div>}

      {!summaryLoading && summary && (
        <>
          {summary.delta_reliability !== 'ok' && summary.delta_reliability_note && (
            <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2.5 text-xs text-amber-900 dark:text-amber-200">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                <strong>
                  {summary.delta_reliability === 'unreliable'
                    ? 'Comparisons are not meaningful on this range.'
                    : 'Treat the comparisons below with caution.'}
                </strong>{' '}
                {summary.delta_reliability_note}
              </span>
            </div>
          )}

          {summary.environment === 'prod' && summary.env_unlabeled_turns > 0 && (
            <div className="text-xs text-gray-500">
              Production view. {fmt(summary.env_unlabeled_turns)} turns in range carry no
              environment label and are included rather than guessed at.
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
            {/* Volume INCLUDES voice conversations -- a voice conversation is a
                conversation and a voice user is a user -- so the share is shown
                rather than left implied. Response time below does the opposite
                and excludes voice; see the note on that card.
                `sub` appears only when there is voice in range, so these cards
                are unchanged for any period before voice shipped. */}
            <Stat
              label="Conversations"
              value={fmt(summary.sessions)}
              sub={summary.voice_sessions > 0 ? `${fmt(summary.voice_sessions)} by voice` : undefined}
              deltaPct={summary.sessions_delta_pct}
              muted={summary.delta_reliability !== 'ok'}
              deltaVs={deltaVs}
            />
            <Stat
              label="Turns"
              value={fmt(summary.turns)}
              sub={summary.voice_turns > 0 ? `${fmt(summary.voice_turns)} by voice` : undefined}
              deltaPct={summary.turns_delta_pct}
              muted={summary.delta_reliability !== 'ok'}
              deltaVs={deltaVs}
            />
            <Stat label="Active Users" value={fmt(summary.users)} deltaPct={summary.users_delta_pct} muted={summary.delta_reliability !== 'ok'}
              deltaVs={deltaVs} />
            <Stat
              label="Response Time (Median)"
              value={secs(summary.p50_seconds)}
              sub={summary.voice_turns > 0 ? 'text turns only' : undefined}
              deltaPct={summary.p50_seconds_prev
                ? Math.round(1000 * (summary.p50_seconds - summary.p50_seconds_prev) / summary.p50_seconds_prev) / 10
                : null}
              lowerIsBetter
              muted={summary.delta_reliability !== 'ok'}
              deltaVs={deltaVs}
            />
            <Stat
              label="Response Time (p95)"
              value={secs(summary.p95_seconds)}
              sub={summary.voice_turns > 0 ? 'text turns only' : undefined}
              deltaPct={summary.p95_seconds_prev
                ? Math.round(1000 * (summary.p95_seconds - summary.p95_seconds_prev) / summary.p95_seconds_prev) / 10
                : null}
              lowerIsBetter
              muted={summary.delta_reliability !== 'ok'}
              deltaVs={deltaVs}
            />
            {/* Two honesty details on this card. The delta guard is != null,
                not truthy — a previous week at exactly 0% positive is a real
                baseline, and the falsy check used to suppress it. And the
                difference is percentage POINTS (deltaUnit), where every other
                card's delta is relative change: both used to render as "X%",
                so 40%→50% positive read identically to 40→44 sessions. */}
            <Stat
              label="Positive Feedback"
              value={summary.positive_feedback_pct === null ? '—' : `${summary.positive_feedback_pct}%`}
              deltaPct={summary.positive_feedback_pct_prev != null && summary.positive_feedback_pct != null
                ? Math.round(10 * (summary.positive_feedback_pct - summary.positive_feedback_pct_prev)) / 10
                : null}
              deltaUnit=" pts"
              muted={summary.delta_reliability !== 'ok'}
              deltaVs={deltaVs}
              sub={`${fmt(summary.rated_total)} rated`}
            />
            <Stat
              label="Days Between Visits"
              value={summary.avg_days_between_sessions === null ? '—' : `${summary.avg_days_between_sessions}d`}
              sub="average gap for people who came back"
            />
            {/* Turn span, not open-to-close: there is no widget-open event yet,
                so this is first turn to last turn — and single-turn sessions
                have no span, so the basis is stated instead of blending them
                in as zeroes. */}
            <Stat
              label="Session Duration"
              value={summary.session_span_p50_seconds == null
                ? '—'
                : `${Math.round(summary.session_span_p50_seconds)}s`}
              sub={summary.span_basis_sessions
                ? `median, first→last turn · ${fmt(summary.span_basis_sessions)} sessions with 2+ turns`
                : 'no multi-turn sessions in range'}
            />
            <Stat
              label="Returning Users"
              value={summary.returning_pct === null ? '—' : `${summary.returning_pct}%`}
              sub={`${fmt(summary.returning_users)} of ${fmt(summary.users)} · avg ${summary.avg_sessions_per_user ?? '—'} sessions each`}
            />
          </div>

          {summary.compared_with && (
            <div className="text-xs text-gray-500">
              Deltas compare against {summary.compared_with.start_date} → {summary.compared_with.end_date},
              the equal-length window immediately before this one.
            </div>
          )}
        </>
      )}

      {/* ── Daily trend ──────────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Daily Usage
          </CardTitle>
        </CardHeader>
        <CardContent>
          {trendLoading && <div className="text-sm text-gray-500 py-8">Loading…</div>}
          {!trendLoading && trend.length === 0 && (
            <div className="text-sm text-gray-500 py-8">No activity in this range.</div>
          )}
          {!trendLoading && trend.length > 0 && (
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={trend} margin={{ top: 8, right: 12, bottom: 4, left: -12 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.25} vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} tickMargin={8} minTickGap={24} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Line type="monotone" dataKey="sessions" stroke="#378ADD" strokeWidth={2} dot={false} name="Conversations" />
                  <Line type="monotone" dataKey="users" stroke="#1D9E75" strokeWidth={2} dot={false} name="Users" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          <p className="pt-2 text-xs text-gray-500 flex gap-2">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>
              Days with no activity are absent rather than plotted as zero, so a gap in the
              line means no traffic was recorded — not that traffic was zero.
            </span>
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">

        {/* ── Tool mix ───────────────────────────────────────────────────── */}
        <Card className="min-w-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Wrench className="h-4 w-4" />
              What the assistant does
            </CardTitle>
          </CardHeader>
          <CardContent>
            {toolsLoading && <div className="text-sm text-gray-500 py-6">Loading…</div>}
            {!toolsLoading && topTools.length === 0 && (
              <div className="text-sm text-gray-500 py-6">No tool calls recorded in this range.</div>
            )}
            {!toolsLoading && topTools.map((t) => {
              const share = totalCalls ? Math.round(1000 * t.calls / totalCalls) / 10 : 0;
              return (
                <div key={t.tool} className="flex items-center gap-3 py-1.5 text-sm">
                  <span className="w-[190px] truncate text-gray-600 dark:text-gray-300" title={t.tool}>
                    {t.tool}
                  </span>
                  <div className="flex-1 h-2 rounded bg-zinc-100 dark:bg-zinc-800 overflow-hidden">
                    <div className="h-full rounded bg-[#378ADD]" style={{ width: `${share}%` }} />
                  </div>
                  <span className="w-12 text-right tabular-nums text-gray-500">{fmt(t.calls)}</span>
                </div>
              );
            })}
            <p className="pt-3 text-xs text-gray-500">
              Full per-tool success rates and latency live on the Quality tab — this is the
              same data, shown as a mix rather than a table.
            </p>
          </CardContent>
        </Card>

        {/* ── The funnel that cannot be built ────────────────────────────── */}
        <Card className="min-w-0 border-dashed">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-gray-500">
              <Filter className="h-4 w-4" />
              Usage Funnel
              <Badge variant="outline" className="font-normal text-[10px] px-1.5 py-0 whitespace-nowrap">
                Not Measurable Yet
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-gray-500 space-y-3">
            <p>
              This card will show the funnel: how many people <strong>opened the assistant →
              asked a question → saw a product → added to cart</strong>. All four events are
              already tracked.
            </p>
            <p>
              The blocker: when someone <em>opens</em> the widget, that event is saved with one
              session ID; when they <em>send a message</em>, it is saved with a different one.
              Same visit, two IDs that never match — so we cannot tell which opens turned into
              conversations, and without that link there is no funnel (and no abandon rate).
            </p>
            <p>
              The fix is small — make both events use the same session ID — and is written for
              web (pending release) and planned for the next mobile SDK. It applies{' '}
              <strong>forward only</strong>: old events keep their mismatched IDs, so the funnel
              starts measuring from the day the fix ships.
            </p>
            <p className="text-xs">
              For the record: an earlier figure claiming almost no opens led to a message was an
              artifact of this exact ID mismatch — not real behavior — and was withdrawn.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
